import { useState, useEffect } from 'react';
import { useNavigate, useLocation, useParams, useSearchParams } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements
} from '@stripe/react-stripe-js';
import axios from 'axios';
import { getAuthConfig, isAuthenticated, logout } from '../utils/auth';
import AppNav from '../components/AppNav';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || 'pk_test_your_key_here');

function RetryPaymentForm({ subscription, clientSecret, requiresAction, onSuccess, onError, service }) {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);

  // Handle 3DS authentication for existing card (no new card needed)
  const handleAuthenticate = async () => {
    if (!stripe) return;
    setProcessing(true);
    setError(null);
    try {
      const { error: actionError, paymentIntent } = await stripe.handleNextAction({ clientSecret });
      if (actionError) {
        setError(actionError.message);
        onError(actionError.message);
      } else if (paymentIntent?.status === 'succeeded') {
        onSuccess(paymentIntent);
      } else if (paymentIntent?.status === 'requires_payment_method') {
        // Card declined after 3DS — ask for new card
        setError('Authentication succeeded but the payment was declined. Please use a different card.');
        onError('Card declined after authentication');
      } else {
        onSuccess(paymentIntent);
      }
    } catch (err) {
      setError('Authentication failed. Please try again.');
      onError(err.message);
    } finally {
      setProcessing(false);
    }
  };

  // Handle new card entry for genuinely failed payments
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setProcessing(true);
    setError(null);
    try {
      const { error: submitError } = await elements.submit();
      if (submitError) {
        setError(submitError.message);
        setProcessing(false);
        return;
      }
      const { error: confirmError, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}/${service}?retry_success=true`,
        },
        redirect: 'if_required'
      });
      if (confirmError) {
        setError(confirmError.message);
        onError(confirmError.message);
      } else if (paymentIntent?.status === 'succeeded') {
        onSuccess(paymentIntent);
      } else {
        onSuccess(paymentIntent);
      }
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
      onError(err.message);
    } finally {
      setProcessing(false);
    }
  };

  // 3DS authentication flow — existing card just needs verification
  if (requiresAction) {
    return (
      <div className="retry-payment-form">
        {error && <div className="alert alert-error">{error}</div>}
        <div className="form-section" style={{ textAlign: 'center', padding: '24px 0' }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>🔐</div>
          <h3 className="section-title" style={{ marginBottom: '12px' }}>Bank Verification Required</h3>
          <p className="payment-method-description" style={{ marginBottom: '8px' }}>
            Your bank requires you to verify this payment using 3D Secure (3DS).
          </p>
          <p className="payment-method-description" style={{ color: '#40E0D0', fontWeight: '600', marginBottom: '24px' }}>
            Amount: ${subscription?.invoice?.amount?.toFixed(2) || '0.00'} {subscription?.invoice?.currency?.toUpperCase() || 'USD'}
          </p>
          <p style={{ color: '#888', fontSize: '13px', marginBottom: '24px' }}>
            No new card details needed — your saved card will be charged after you complete the security check.
          </p>
        </div>
        <button
          type="button"
          className="btn-subscribe"
          onClick={handleAuthenticate}
          disabled={!stripe || processing}
        >
          {processing ? (
            <><span className="spinner"></span>Opening security check...</>
          ) : (
            'Verify Payment Now'
          )}
        </button>
      </div>
    );
  }

  // Standard new-card entry flow
  return (
    <form onSubmit={handleSubmit} className="retry-payment-form">
      {error && <div className="alert alert-error">{error}</div>}
      <div className="form-section">
        <h3 className="section-title">Update Payment Method</h3>
        <p className="payment-method-description">
          Enter a new payment method to retry the failed payment.
        </p>
        <PaymentElement />
      </div>
      <button
        type="submit"
        className="btn-subscribe"
        disabled={!stripe || processing}
      >
        {processing ? (
          <><span className="spinner"></span>Processing Payment...</>
        ) : (
          `Pay $${subscription?.invoice?.amountDue?.toFixed(2) || '0.00'}`
        )}
      </button>
    </form>
  );
}

function RetryPaymentPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { service } = useParams();
  const [subscription, setSubscription] = useState(null);
  const [clientSecret, setClientSecret] = useState('');
  const [requiresAction, setRequiresAction] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const API_URL = process.env.REACT_APP_API_URL || window.location.origin;

  const [searchParams] = useSearchParams();

  useEffect(() => {
    if (!isAuthenticated()) {
      navigate(`/${service}`);
      return;
    }

    // Prefer navigation state (in-app), fall back to ?sub= query param (email link)
    const stateSub = location.state?.subscription;
    if (stateSub) {
      setSubscription(stateSub);
      initializeRetryPayment(stateSub);
      return;
    }

    const subId = searchParams.get('sub');
    if (subId) {
      fetchSubscriptionById(subId);
      return;
    }

    setError('No subscription selected for payment retry');
    setLoading(false);
  }, [location.state, searchParams, navigate]);

  const fetchSubscriptionById = async (subId) => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${API_URL}/api/subscriptions/${subId}`,
        getAuthConfig()
      );
      const sub = response.data;
      setSubscription(sub);
      initializeRetryPayment(sub);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load subscription details');
      setLoading(false);
    }
  };

  const initializeRetryPayment = async (sub) => {
    try {
      setLoading(true);
      setError(null);

      const response = await axios.post(
        `${API_URL}/api/subscriptions/retry-payment`,
        { subscriptionId: sub.id },
        getAuthConfig()
      );

      if (response.data.success && response.data.clientSecret) {
        setClientSecret(response.data.clientSecret);
        // Detect 3DS required: paymentIntent status is requires_action
        if (response.data.paymentIntentStatus === 'requires_action') {
          setRequiresAction(true);
        }
      } else if (response.data.message === 'Invoice is already paid' ||
                 response.data.message === 'Payment already succeeded') {
        setSuccess(true);
      } else {
        setError(response.data.error || 'Failed to initialize payment');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to initialize payment retry');
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentSuccess = async (paymentIntent) => {
    try {
      // Confirm the payment was successful
      const response = await axios.post(
        `${API_URL}/api/subscriptions/confirm-retry-payment`,
        {
          subscriptionId: subscription.id,
          paymentIntentId: paymentIntent?.id
        },
        getAuthConfig()
      );

      if (response.data.success) {
        setSuccess(true);
      }
    } catch (err) {
      // Payment might still be successful even if confirmation fails
      setSuccess(true);
    }
  };

  const handlePaymentError = (errorMessage) => {
    setError(errorMessage);
  };

  if (loading) {
    return (
      <div className="checkout-container-new">
      <AppNav onLogout={() => {}} />
        <div className="loading-container">
          <div className="loading-spinner"></div>
        </div>
        <p className="loading-text">Initializing payment...</p>
      </div>
    );
  }

  if (success) {
    return (
      <div className="checkout-container-new">
      <AppNav onLogout={() => {}} />
        <div className="success-container" style={{
          maxWidth: '500px',
          margin: '40px auto',
          padding: '40px',
          background: '#2d2d2d',
          borderRadius: '12px',
          textAlign: 'center'
        }}>
          <div style={{
            width: '80px',
            height: '80px',
            background: 'linear-gradient(135deg, #40E0D0 0%, #00CED1 100%)',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 24px',
            fontSize: '40px',
            color: 'white'
          }}>
            <i className="fa-solid fa-check"></i>
          </div>
          <h1 style={{ color: '#fff', marginBottom: '16px' }}>Payment Successful!</h1>
          <p style={{ color: '#999', marginBottom: '24px' }}>
            Your subscription has been reactivated. Thank you for your payment.
          </p>
          <button
            onClick={() => navigate(`/${service}`)}
            className="btn-subscribe"
            style={{ maxWidth: '200px', margin: '0 auto' }}
          >
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }

  if (error && !clientSecret) {
    return (
      <div className="checkout-container-new">
      <AppNav onLogout={() => {}} />
        <div className="error-container-full">
          <div className="error-icon"><i className="fa-solid fa-triangle-exclamation"></i></div>
          <h1 className="error-title">Payment Error</h1>
          <p className="error-message">{error}</p>
          <button onClick={() => navigate(`/${service}`)} className="btn-primary-new">
            Return to Dashboard
          </button>
        </div>
      </div>
    );
  }

  const appearance = {
    theme: 'night',
    variables: {
      colorPrimary: '#40E0D0',
      colorBackground: '#2d2d2d',
      colorText: '#ffffff',
      colorDanger: '#ff6b6b',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      spacingUnit: '4px',
      borderRadius: '8px',
    },
  };

  const options = {
    clientSecret,
    appearance,
  };

  return (
    <div className="checkout-container-new">
      <AppNav onLogout={() => {}} />
      <div className="checkout-layout">
        {/* Left side - Form */}
        <div className="checkout-main">
          <h1 className="page-title">Retry Payment</h1>
          <p style={{ color: '#999', marginBottom: '24px' }}>
            {requiresAction
              ? 'Your bank requires a security verification to process this payment.'
              : 'Your previous payment failed. Please update your payment method to continue your subscription.'}
          </p>

          {clientSecret ? (
            <Elements stripe={stripePromise} options={options}>
              <RetryPaymentForm
                subscription={subscription}
                clientSecret={clientSecret}
                requiresAction={requiresAction}
                onSuccess={handlePaymentSuccess}
                onError={handlePaymentError}
                service={service}
              />
            </Elements>
          ) : (
            <div className="checkout-form-new">
              {error && (
                <div className="alert alert-error">
                  {error}
                </div>
              )}
              <div className="loading-container">
                <div className="loading-spinner"></div>
                <p className="helper-text">Initializing payment...</p>
              </div>
            </div>
          )}
        </div>

        {/* Right side - Subscription Summary */}
        <div className="checkout-sidebar">
          <div className="plan-card">
            <div style={{
              background: 'rgba(255, 107, 107, 0.1)',
              border: '1px solid rgba(255, 107, 107, 0.3)',
              borderRadius: '8px',
              padding: '12px',
              marginBottom: '16px'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                <i className="fa-solid fa-exclamation-circle" style={{ color: '#ff6b6b', fontSize: '16px' }}></i>
                <span style={{ color: '#ff6b6b', fontWeight: '600', fontSize: '14px' }}>
                  Payment Failed
                </span>
              </div>
              <p style={{ margin: '0', color: '#cccccc', fontSize: '12px', lineHeight: '1.5' }}>
                Please update your payment method to reactivate your subscription.
              </p>
            </div>

            <h2 className="plan-card-title">{subscription?.planName || 'Subscription'}</h2>

            {subscription?.domain && (
              <div style={{
                background: '#1a1a1a',
                padding: '12px',
                borderRadius: '8px',
                marginBottom: '16px'
              }}>
                <span style={{ color: '#999', fontSize: '12px', display: 'block', marginBottom: '4px' }}>
                  Domain
                </span>
                <span style={{ color: '#40E0D0', fontWeight: '500' }}>
                  {subscription.domain}
                </span>
              </div>
            )}

            <div className="plan-pricing">
              <div className="pricing-row">
                <span className="pricing-label">Status</span>
                <span className="pricing-value" style={{ color: '#ff6b6b' }}>
                  {subscription?.status === 'past_due' ? 'Past Due' : 'Payment Failed'}
                </span>
              </div>
              <div className="pricing-divider"></div>
              <div className="pricing-row pricing-total">
                <span className="pricing-label">Amount Due</span>
                <span className="pricing-value">
                  ${subscription?.invoice?.amountDue?.toFixed(2) || '0.00'}
                </span>
              </div>
            </div>

            {subscription?.invoice?.hostedInvoiceUrl && (
              <a
                href={subscription.invoice.hostedInvoiceUrl}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'block',
                  textAlign: 'center',
                  color: '#40E0D0',
                  fontSize: '14px',
                  marginTop: '16px',
                  textDecoration: 'none'
                }}
              >
                <i className="fa-solid fa-external-link"></i> View Invoice on Stripe
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default RetryPaymentPage;
